/* ModalCredits.js */
/* @author: anghello */

import React, { useCallback, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Strings from '../../translations/Translations';
import cx from 'classnames';
import { Subscriptions as subscriptions } from '../../enums/Subscriptions';
import { CreditsPackages, CreditsPackages as creditsPackages } from '../../enums/CreditsPackages';
import { useAuth } from '../../context/auth/AuthContext';
import PSO from '../../services/PSO';
import { toast } from 'react-toastify';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import SubscriptionsStatuses from '../../enums/SubscriptionsStatuses';
import CheckoutFormSubscription from './stripe/CheckoutFormSubscription';
import CheckoutFormPackage from './stripe/CheckoutFormPackage';
import './modalcredits.scss';

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
const APP_ENV = process.env.REACT_APP_ENV;
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

export default function ModalCredits() {
  const {
    openModalSubscribe,
    setOpenModalSubscribe,
    openModalCancelSubscription,
    setOpenModalCancelSubscription,
    showMessageNoCredits,
    toggleLoading,
    authenticated,
    getUserDataCredits,
    getUserBalance,
    globalCreditsUserData,
    showMessageAiOperationsLimit
  } = useAuth();

  const toggleSubscribe = useCallback(() => setOpenModalSubscribe(!openModalSubscribe), [openModalSubscribe, setOpenModalSubscribe]);
  const toggleCancel = useCallback(() => setOpenModalCancelSubscription(!openModalCancelSubscription), [openModalCancelSubscription, setOpenModalCancelSubscription]);

  const [creditsPricingType, setCreditsPricingType] = useState('monthly');

  const [stripeOptions, setStripeOptions] = useState();
  const [stripeBuying, setStripeBuying] = useState();

  const [openModalStripe, setOpenModalStripe] = useState(false);
  const toggleModalStripe = useCallback(() => {
    setOpenModalStripe(!openModalStripe);
  }, [openModalStripe]);

  const [openModalWarningSubscriptions, setOpenModalWarningSubscriptions] = useState(false);
  const toggleModalWarningSubscriptions = useCallback(() => {
    setOpenModalWarningSubscriptions(!openModalWarningSubscriptions);
  }, [openModalWarningSubscriptions]);

  const cancelSubscription = useCallback(async () => {
    toggleLoading(true);

    try {
      const response = await PSO.cancelSubscription({ un: authenticated.un, al: authenticated.al });
      if (response && response.result === 'success') {
        toast.success(Strings('subscription_canceled'));

        // await getUserDataCredits();
        // await getUserBalance();

        // setOpenModalCancelSubscription(false);

        window.location.reload();
      } else {
        const message = (response && response.error) ? response.error : Strings('error_on_update');
        toast.error(message);
      }
    } catch (err) {
      toast.error(Strings('general_error'));
    }

    toggleLoading(false);
  }, [toggleLoading, authenticated]);

  const startSubscription = useCallback(async (type) => {
    toggleLoading(true);

    let priceID, subscription, regularity;

    if (type === 'free') {
      subscription = SubscriptionsStatuses.TYPE.FREE;
      regularity = SubscriptionsStatuses.REGULARITY.FREE;
    } else if (type === 'plus') {
      subscription = SubscriptionsStatuses.TYPE.PLUS;

      if (APP_ENV === 'dev' || APP_ENV === 't11') {
        if (creditsPricingType === 'monthly') {
          priceID = subscriptions.plus.stripe_price_id_monthly_test;
        } else if (creditsPricingType === 'yearly') {
          priceID = subscriptions.plus.stripe_price_id_annual_test;
        }
      } else if (APP_ENV === 'prod') {
        if (creditsPricingType === 'monthly') {
          priceID = subscriptions.plus.stripe_price_id_monthly_production;
        } else if (creditsPricingType === 'yearly') {
          priceID = subscriptions.plus.stripe_price_id_annual_production;
        }
      }
    } else if (type === 'premium') {
      subscription = SubscriptionsStatuses.TYPE.PREMIUM;

      if (APP_ENV === 'dev' || APP_ENV === 't11') {
        if (creditsPricingType === 'monthly') {
          priceID = subscriptions.premium.stripe_price_id_monthly_test;
        } else if (creditsPricingType === 'yearly') {
          priceID = subscriptions.premium.stripe_price_id_annual_test;
        }
      } else if (APP_ENV === 'prod') {
        if (creditsPricingType === 'monthly') {
          priceID = subscriptions.premium.stripe_price_id_monthly_production;
        } else if (creditsPricingType === 'yearly') {
          priceID = subscriptions.premium.stripe_price_id_annual_production;
        }
      }
    } else if (type === 'max') {
      subscription = SubscriptionsStatuses.TYPE.MAX;

      if (APP_ENV === 'dev' || APP_ENV === 't11') {
        if (creditsPricingType === 'monthly') {
          priceID = subscriptions.max.stripe_price_id_monthly_test;
        } else if (creditsPricingType === 'yearly') {
          priceID = subscriptions.max.stripe_price_id_annual_test;
        }
      } else if (APP_ENV === 'prod') {
        if (creditsPricingType === 'monthly') {
          priceID = subscriptions.max.stripe_price_id_monthly_production;
        } else if (creditsPricingType === 'yearly') {
          priceID = subscriptions.max.stripe_price_id_annual_production;
        }
      }
    }

    if (creditsPricingType === 'monthly') {
      regularity = SubscriptionsStatuses.REGULARITY.MONTHLY;
    } else if (creditsPricingType === 'yearly') {
      regularity = SubscriptionsStatuses.REGULARITY.YEARLY;
    }

    if (priceID && subscription && regularity) {
      toggleLoading(true);

      const { un, al } = authenticated;
      let subscriptionID, clientSecret;

      // Creating subscription
      try {

        const response = await PSO.createSubscription({ un, al, priceID, subscription, regularity });
        if (response && response.result && response.result === 'success' && response.subscriptionID && response.clientSecret) {
          subscriptionID = response.subscriptionID;
          clientSecret = response.clientSecret;
        } else {
          toast.error(Strings('unable_to_create_subscription'));
        }
      } catch (err) {
        console.error(err);
      }

      if (subscriptionID && clientSecret) {
        setStripeBuying('subscription');
        setStripeOptions({ clientSecret });
        setOpenModalStripe(true);
      }

      toggleLoading(false);
    }

    toggleLoading(false);
  }, [toggleLoading, creditsPricingType, authenticated]);

  const callbackStripeSuccess = useCallback(async () => {
    toggleLoading(true);
    await getUserDataCredits();
    await getUserBalance();
    toggleLoading(false);

    if (globalCreditsUserData && globalCreditsUserData.subscriptions && globalCreditsUserData.subscriptions.length) {
      let hasToOpenModal = false;
      globalCreditsUserData.subscriptions.forEach((item) => {
        if (item.client !== 'HTML') {
          hasToOpenModal = true;
        }
      });
      if (hasToOpenModal) {
        setOpenModalWarningSubscriptions(true);
      }
    }

    setOpenModalStripe(false);
    setOpenModalSubscribe(false);
  }, [setOpenModalSubscribe, getUserDataCredits, getUserBalance, toggleLoading, globalCreditsUserData]);

  const callbackStripeSuccessPackage = useCallback(async ({ balance, priceID, paymentIntent }) => {
    toggleLoading(true);

    // Registering CREDITS Package
    try {
      const { un, al } = authenticated;
      const response = await PSO.registerCreditsPackage({ un, al, balance, productID: priceID, transactionID: paymentIntent });
      if (response && response.result && response.result === 'success') {
        
      } else {
        toast.error(Strings('unable_to_buy_credits'));
      }
    } catch (err) {
      console.error(err);
    }

    await getUserDataCredits();
    await getUserBalance();

    toggleLoading(false);

    setOpenModalStripe(false);
    setOpenModalSubscribe(false);
  }, [setOpenModalSubscribe, getUserDataCredits, getUserBalance, toggleLoading, authenticated]);

  const startPackage = useCallback(async (type) => {
    toggleLoading(true);

    let priceID, price, balance;

    if (type === 'package1') {
      price = CreditsPackages.package1.price;
      balance = CreditsPackages.package1.credits;
      if (APP_ENV === 'dev' || APP_ENV === 't11') {
        priceID = CreditsPackages.package1.stripe_price_id_test;
      } else if (APP_ENV === 'prod') {
        priceID = CreditsPackages.package1.stripe_price_id_production;
      }
    } else if (type === 'package2') {
      price = CreditsPackages.package2.price;
      balance = CreditsPackages.package2.credits;
      if (APP_ENV === 'dev' || APP_ENV === 't11') {
        priceID = CreditsPackages.package2.stripe_price_id_test;
      } else if (APP_ENV === 'prod') {
        priceID = CreditsPackages.package2.stripe_price_id_production;
      }
    } else if (type === 'max') {
      price = CreditsPackages.package3.price;
      balance = CreditsPackages.package3.credits;
      if (APP_ENV === 'dev' || APP_ENV === 't11') {
        priceID = CreditsPackages.package3.stripe_price_id_test;
      } else if (APP_ENV === 'prod') {
        priceID = CreditsPackages.package3.stripe_price_id_production;
      }
    }

    if (priceID && price) {
      toggleLoading(true);

      const { un, al } = authenticated;
      let paymentIntent, clientSecret;

      // Creating CREDITS Package
      try {
        const response = await PSO.createCreditsPackage({ un, al, priceID, amount: price });
        if (response && response.result && response.result === 'success' && response.paymentIntent && response.clientSecret) {
          paymentIntent = response.paymentIntent;
          clientSecret = response.clientSecret;
        } else {
          toast.error(Strings('unable_to_buy_credits'));
        }
      } catch (err) {
        console.error(err);
      }

      if (paymentIntent && clientSecret) {
        window.pz_smm_package_buy_payment_intent = {
          balance,
          priceID,
          paymentIntent
        };
        setStripeBuying('package');
        setStripeOptions({ clientSecret });
        setOpenModalStripe(true);
      }

      toggleLoading(false);
    }
  }, [authenticated, toggleLoading]);

  const getMaxSubscriptionType = useCallback((subscriptions) => {
    let maxSubscriptionType = 0;
    if (subscriptions && subscriptions.length) {
      subscriptions.forEach((item) => {
        if (parseInt(item.type) > maxSubscriptionType && parseInt(item.status) === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY) {
          maxSubscriptionType = parseInt(item.type);
        }
      });
    }
    return maxSubscriptionType;
  }, []);

  const getSubscriptionDisabled = useCallback((type) => {
    let disabled = false;

    if (type === 'free') { type = SubscriptionsStatuses.TYPE.FREE; }
    else if (type === 'plus') { type = SubscriptionsStatuses.TYPE.PLUS; }
    else if (type === 'premium') { type = SubscriptionsStatuses.TYPE.PREMIUM; }
    else if (type === 'max') { type = SubscriptionsStatuses.TYPE.MAX; }

    if (globalCreditsUserData && globalCreditsUserData.subscriptions) {
      const maxSubscriptionType = getMaxSubscriptionType(globalCreditsUserData.subscriptions);
      globalCreditsUserData.subscriptions.forEach((item) => {
        if (parseInt(item.type) === type && parseInt(item.status) === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY) {
          disabled = true;
        } else if (maxSubscriptionType === 0 && type === SubscriptionsStatuses.TYPE.FREE) {
          disabled = true;
        }
      });
    }

    return disabled;
  }, [globalCreditsUserData, getMaxSubscriptionType]);

  const getSubscriptionLabel = useCallback((type) => {
    let label = Strings('subscribe_now');
    // let label = Strings('your_plan');

    if (type === 'free') { type = SubscriptionsStatuses.TYPE.FREE; }
    else if (type === 'plus') { type = SubscriptionsStatuses.TYPE.PLUS; }
    else if (type === 'premium') { type = SubscriptionsStatuses.TYPE.PREMIUM; }
    else if (type === 'max') { type = SubscriptionsStatuses.TYPE.MAX; }

    if (globalCreditsUserData && globalCreditsUserData.subscriptions) {
      const maxSubscriptionType = getMaxSubscriptionType(globalCreditsUserData.subscriptions);
      globalCreditsUserData.subscriptions.forEach((item) => {
        const itemType = parseInt(item.type);
        if (maxSubscriptionType === 0 && type === SubscriptionsStatuses.TYPE.FREE) {
          label = Strings('your_plan');
        } else if (itemType === type && parseInt(item.status) === SubscriptionsStatuses.STATUS.ACTIVE_MONTHLY) {
          label = Strings('your_plan');
        } else if (type > maxSubscriptionType) {
          label = Strings('upgrade');
        } else if (type < maxSubscriptionType) {
          label = Strings('downgrade_subscription');
        }
      });
    }

    return label;
  }, [globalCreditsUserData, getMaxSubscriptionType]);

  return (
    <>
      <Modal className='pb-modal-1' isOpen={openModalSubscribe} toggle={toggleSubscribe} size='xl'>
        <ModalHeader toggle={toggleSubscribe}>
          {Strings('pepblast_credits')}
        </ModalHeader>
        <ModalBody>
          {(showMessageNoCredits || showMessageAiOperationsLimit) && (
            <div className='row mb-3'>
              <div className='col'>
                <div className="alert alert-primary text-center" role="alert">
                  {showMessageNoCredits && Strings('no_credits_message')}
                  {showMessageAiOperationsLimit && Strings('ai_operations_limit_message')}
                </div>
              </div>
            </div>
          )}

          <div className='row justify-content-center mb-5'>
            <div className='col-md-8'>
              <h3 className='text-center'>{Strings('credits_text_1')}</h3>
              <p className='text-center text-muted fs-5'>{Strings('credits_text_2')}</p>
            </div>
          </div>

          <div className="row">
            <div className='col-12 d-flex justify-content-center gap-2 mb-4'>
              <div>
                <h6>{Strings('monthly_billing')}</h6>
              </div>
              <div
                className="switch has-switch mb-2 mr-2"
                data-on-label={Strings('yearly')}
                data-off-label={Strings('monthly')}
                onClick={() => setCreditsPricingType(current => (current === 'monthly') ? 'yearly' : 'monthly')}
              >
                <div
                  className={cx("switch-animate", {
                    'switch-on': creditsPricingType === 'yearly',
                    'switch-off': creditsPricingType === 'monthly'
                  })}
                >
                  <input type="checkbox" />
                  <span className="switch-left">{Strings('year')}</span>
                  <label>&nbsp;</label>
                  <span className="switch-right">{Strings('month')}</span>
                </div>
              </div>
              <div>
                <h6>{Strings('annual_billing')}</h6>
              </div>
            </div>

            <div className="col-sm-12 col-md-3">
              <div className="card mb-4 box-shadow">
                <div className="card-header">
                  <h4 className="my-0 font-weight-normal">{subscriptions.free.title}</h4>
                  <h6 className='text-muted'>{Strings('plans_description_free')}</h6>
                </div>
                <div className="card-body">
                  <h1 className="card-title pricing-card-title">
                    {creditsPricingType === 'monthly' && <span>${subscriptions.free.pricing_month} </span>}
                    {creditsPricingType === 'yearly' && <span>${subscriptions.free.pricing_year} </span>}
                    {/* <small className="text-muted"> / {Strings('mo')}</small> */}
                  </h1>
                  <ul className="list-unstyled mt-3 mb-4">
                    <li>{subscriptions.free.credits_month} {Strings('credits_month')}</li>
                    <li>{subscriptions.free.max_operations} {Strings('ai_task_at_once')}</li>
                  </ul>
                  <Button
                    color='secondary'
                    // className="btn btn-lg btn-block pb-outline-btn-3"
                    onClick={() => { startSubscription('free'); }}
                    disabled={getSubscriptionDisabled('free')}
                  >
                    {getSubscriptionLabel('free')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-3">
              <div className="card mb-4 box-shadow">
                <div className="card-header">
                  <h4 className="my-0 font-weight-normal">{subscriptions.plus.title}</h4>
                  <h6 className='text-muted'>{Strings('plans_description_plus')}</h6>
                </div>
                <div className="card-body">
                  <h1 className="card-title pricing-card-title">
                    {creditsPricingType === 'monthly' && <span>${subscriptions.plus.pricing_month} </span>}
                    {creditsPricingType === 'yearly' && <span>${subscriptions.plus.pricing_year} </span>}
                    <small className="text-muted"> / {Strings('mo')}</small>
                  </h1>
                  {creditsPricingType === 'yearly' && (
                    <div><small className='text-muted'>$ {subscriptions.plus.total_year} total</small></div>
                  )}
                  <ul className="list-unstyled mt-3 mb-4">
                    <li><strong>{subscriptions.plus.credits_month}</strong> {Strings('credits_month')}</li>
                    <li><strong>{subscriptions.plus.max_operations}</strong> {Strings('parallel_ai_tasks')}</li>
                  </ul>
                  <Button
                    color='primary'
                    // className="btn btn-lg btn-block btn-primary pb-btn-1"
                    onClick={() => { startSubscription('plus'); }}
                    disabled={getSubscriptionDisabled('plus')}
                  >
                    {getSubscriptionLabel('plus')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-3">
              <div className="card mb-4 box-shadow">
                <div className="card-header">
                  <h4 className="my-0 font-weight-normal">{subscriptions.premium.title}</h4>
                  <h6 className='text-muted'>{Strings('plans_description_premium')}</h6>
                </div>
                <div className="card-body">
                  <h1 className="card-title pricing-card-title">
                    {creditsPricingType === 'monthly' && <span>${subscriptions.premium.pricing_month} </span>}
                    {creditsPricingType === 'yearly' && <span>${subscriptions.premium.pricing_year} </span>}
                    <small className="text-muted"> / {Strings('mo')}</small>
                  </h1>
                  {creditsPricingType === 'yearly' && (
                    <div><small className='text-muted'>$ {subscriptions.premium.total_year} total</small></div>
                  )}
                  <ul className="list-unstyled mt-3 mb-4">
                    <li><strong>{subscriptions.premium.credits_month}</strong> {Strings('credits_month')}</li>
                    <li><strong>{subscriptions.premium.max_operations}</strong> {Strings('parallel_ai_tasks')}</li>
                  </ul>
                  <Button
                    color='primary'
                    // className="btn btn-lg btn-block btn-primary pb-btn-1"
                    onClick={() => { startSubscription('premium'); }}
                    disabled={getSubscriptionDisabled('premium')}
                  >
                    {getSubscriptionLabel('premium')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-3">
              <div className="card mb-4 box-shadow">
                <div className="card-header">
                  <h4 className="my-0 font-weight-normal">{subscriptions.max.title}</h4>
                  <h6 className='text-muted'>{Strings('plans_description_max')}</h6>
                </div>
                <div className="card-body">
                  <h1 className="card-title pricing-card-title">
                    {creditsPricingType === 'monthly' && <span>${subscriptions.max.pricing_month} </span>}
                    {creditsPricingType === 'yearly' && <span>${subscriptions.max.pricing_year} </span>}
                    <small className="text-muted"> / {Strings('mo')}</small>
                  </h1>
                  {creditsPricingType === 'yearly' && (
                    <div><small className='text-muted'>$ {subscriptions.max.total_year} total</small></div>
                  )}
                  <ul className="list-unstyled mt-3 mb-4">
                    <li><strong>{subscriptions.max.credits_month}</strong> {Strings('credits_month')}</li>
                    <li><strong>{subscriptions.max.max_operations}</strong> {Strings('parallel_ai_tasks')}</li>
                  </ul>
                  <Button
                    color='primary'
                    // className="btn btn-lg btn-block btn-primary pb-btn-1"
                    onClick={() => { startSubscription('max'); }}
                    disabled={getSubscriptionDisabled('max')}
                  >
                    {getSubscriptionLabel('max')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className='my-5'>
            <hr />
          </div>

          <div className='row justify-content-center mb-5'>
            <div className='col-md-8'>
              <h3 className='text-center'>{Strings('packs')}</h3>
              <p className='text-center text-muted fs-5'>{Strings('get_pepblast_credits_directly_with_credit_packs')}</p>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12 col-md-4">
              <div className="card mb-4 box-shadow">
                <div className="card-header">
                  <h4 className="my-0 font-weight-normal">{creditsPackages.package1.title}</h4>
                  <h6 className='text-muted'>{creditsPackages.package1.credits} {Strings('credits_total')}</h6>
                </div>
                <div className="card-body">
                  <h1 className="card-title pricing-card-title mb-4">
                    <span>${creditsPackages.package1.price}</span>
                  </h1>
                  <Button color='primary' onClick={() => startPackage('package1')}>
                    {Strings('get_pack')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-4">
              <div className="card mb-4 box-shadow">
                <div className="card-header">
                  <h4 className="my-0 font-weight-normal">{creditsPackages.package2.title}</h4>
                  <h6 className='text-muted'>{creditsPackages.package2.credits} {Strings('credits_total')}</h6>
                </div>
                <div className="card-body">
                  <h1 className="card-title pricing-card-title mb-4">
                    <span>${creditsPackages.package2.price}</span>
                  </h1>
                  <Button color='primary' onClick={() => startPackage('package2')}>
                    {Strings('get_pack')}
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-sm-12 col-md-4">
              <div className="card mb-4 box-shadow">
                <div className="card-header">
                  <h4 className="my-0 font-weight-normal">{creditsPackages.package3.title}</h4>
                  <h6 className='text-muted'>{creditsPackages.package3.credits} {Strings('credits_total')}</h6>
                </div>
                <div className="card-body">
                  <h1 className="card-title pricing-card-title mb-4">
                    <span>${creditsPackages.package3.price}</span>
                  </h1>
                  <Button color='primary' onClick={() => startPackage('package3')}>
                    {Strings('get_pack')}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className='row mt-5'>
            <div className='col'>
              <p className='text-center text-muted'>{Strings('pepblast_credits_copyright')}</p>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Modal STRIPE */}
      <Modal isOpen={openModalStripe} toggle={toggleModalStripe} size='md'>
        <ModalHeader toggle={toggleModalStripe}>
          {Strings('payment_confirmation')}
        </ModalHeader>
        <ModalBody>
          {stripeOptions && stripeBuying && stripeBuying === 'subscription' && (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <CheckoutFormSubscription callback={callbackStripeSuccess} />
            </Elements>
          )}
          {stripeOptions && stripeBuying && stripeBuying === 'package' && (
            <Elements stripe={stripePromise} options={stripeOptions}>
              <CheckoutFormPackage callback={callbackStripeSuccessPackage} />
            </Elements>
          )}
        </ModalBody>
      </Modal>

      {/* Modal Cancel */}
      <Modal isOpen={openModalCancelSubscription} toggle={toggleCancel} size='md'>
        <ModalHeader toggle={toggleCancel}>
          {Strings('confirm_cancel')}
        </ModalHeader>
        <ModalBody>
          <p><strong>{Strings('cancel_text_1')}</strong></p>
          <p>{Strings('cancel_text_2')}</p>
          <p>{Strings('cancel_text_3')}</p>
          <p><strong>{Strings('cancel_text_4')}</strong></p>
          <p>{Strings('cancel_text_5')}</p>
          <p><strong>{Strings('cancel_text_6')}</strong></p>
          <p>{Strings('cancel_text_7')}</p>
          <p><strong>{Strings('cancel_text_8')}</strong></p>
          <p>{Strings('cancel_text_9')}</p>
          <hr />
          <p>{Strings('cancel_text_10')}</p>
        </ModalBody>
        <ModalFooter className='d-flex justify-content-between'>
          <Button color='secondary' onClick={toggleCancel}>
            {Strings('no')}
          </Button>
          <Button color='danger' onClick={cancelSubscription}>
            {Strings('yes_cancel')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Warning Subscriptions */}
      <Modal isOpen={openModalWarningSubscriptions} toggle={toggleModalWarningSubscriptions} size='md'>
        <ModalHeader toggle={toggleModalWarningSubscriptions}>
          {Strings('attention')}
        </ModalHeader>
        <ModalBody>
          <p><strong>{Strings('warning_text_1')}</strong></p>
        </ModalBody>
        <ModalFooter className='d-flex justify-content-between'>
          <Button
            size="md"
            color="secondary"
            onClick={toggleModalWarningSubscriptions}
          >
            {Strings('ok_got_it')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}